import React from 'react';
import { connect } from 'react-redux';
import { InView } from 'react-intersection-observer';
import Chart from 'react-apexcharts';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import VanillaContextMenu from 'vanilla-context-menu';
import axiosInstance from 'utils/axiosUtil';
import { withIsMobile } from 'utils';
import fileDownload from 'js-file-download';
import moment from 'moment';

import {
  TitleTooltip,
  LoadingView,
  SmallIconTextButton,
  ServiceLineSelect,
  PatientTypeSelect,
} from 'components';

import {
  CsvUtil,
  DataUtil,
  StoreUtil,
  SpecialtyUtil,
  PatientTypes,
  PatientType,
} from 'doctivity-shared/utils';
import { ColorUtil, ServiceLineUtil, withRouter } from 'utils';
import { loadAnalytics } from 'store/actions/analyticsActions';
import {
  queryProviders,
  API_PROVIDERS_LIST,
} from 'store/actions/providersActions';

const styles = (theme) => ({
  card: {},
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1),
  },
  provider2: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  leftIcon: {
    marginRight: theme.spacing(),
  },
  filterDropDown: {
    width: 320,
    marginRight: theme.spacing(),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  specialtyDropDown: {
    width: 180,
    marginRight: theme.spacing(),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  dropDown: {
    marginBottom: theme.spacing(2),
  },
  dropDownContainer: {
    display: 'flex',
    flexDirection: 'row',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  filterLabel: {
    backgroundColor: 'white',
    paddingRight: 4,
  },
  noData: {
    textAlign: 'center',
    paddingTop: 50,
    minHeight: 200,
  },
  contextMenu: {
    background: '#3c3c3c',
    opacity: 0.93,
    fontFamily: 'Roboto',
    // text color for each item
    '& > *:not(hr)': {
      '&:hover': {
        background: '#e67e22',
      },
      fontSize: 13,
    },
  },
  colorKey: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: 12,
    fontWeight: 400,
    minHeight: 40,
    [theme.breakpoints.down('md')]: {
      flexWrap: 'wrap',
    },
  },
  colorKeyIcon: {
    width: 14,
    height: 14,
    marginRight: 2,
    borderRadius: 2,
  },
  colorKeyText: {
    marginRight: 12,
  },
});

class ClaimsNetwork extends React.Component {
  constructor(props) {
    super(props);

    this.cardElement = React.createRef(null);

    this.state = {
      serviceline: 'all',
      before: false,
      provider2: null,
      specialty: 'all',
      inView: false,
    };
  }

  componentDidMount() {
    const { network } = this.props;

    if (StoreUtil.needsLoadNoCache(network)) {
      this.fetchAllData();
    } else {
      this.fetchNetworkAffiliated();
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.provider !== prevProps.provider ||
      this.props.patientType !== prevProps.patientType
    ) {
      this.fetchAllData();
    }

    if (
      !StoreUtil.isLoaded(prevProps.network) &&
      StoreUtil.isLoaded(this.props.network)
    ) {
      this.fetchNetworkAffiliated();
    }
  }

  fetchAllData() {
    this.fetchData();
    this.fetchSpecialtiesData();
  }

  fetchData(csv) {
    const {
      dispatch,
      providers,
      provider,
      patientType,
      servicelines,
      network,
      clientId,
    } = this.props;

    const { before, serviceline } = this.state;

    if (providers?.list) {
      StoreUtil.setInvalid(providers, StoreUtil.COMMON_LIST);
    }

    const options = {
      type: 'CLAIMS_NETWORK',
      filter: {
        provider_id: provider.id,
        serviceline:
          this.state.serviceline === 'all' || this.state.provider2
            ? undefined
            : this.state.serviceline,
        provider_id_2: this.state.provider2
          ? this.state.provider2.id
          : undefined,
        before,
        taxonomy_code:
          this.state.specialty === 'all' ? undefined : this.state.specialty,
        patient_type: patientType,
      },
      opts: {
        limit: 25,
        offset: 0,
      },
    };

    if (!csv) {
      dispatch(loadAnalytics(options));
    } else {
      axiosInstance
        .post('/Analytics', {
          ...options,
          format: 'csv',
          headers: {
            'Content-Type': 'text/csv',
          },
        })
        .then((response) => {
          const providerKey = before ? 'provider1' : 'provider2';
          const networkProviderIds = network.data.rows.map(
            (network) => network[providerKey].id
          );
          axiosInstance
            .get('/Providers', {
              params: {
                opts: {
                  where: { id: networkProviderIds },
                  include: [
                    {
                      association: 'clients',
                      attributes: ['id'],
                      through: { attributes: [] }, // don't need the join table data
                      where: {
                        id: clientId,
                      },
                      duplicating: false,
                      paranoid: false,
                      required: true,
                    },
                  ],
                },
              },
              format: 'csv',
              headers: {
                'Content-Type': 'text/csv',
              },
            })
            .then((affiliatedResponse) => {
              const servicelineName =
                serviceline !== 'all'
                  ? ServiceLineUtil.findServiceLine(servicelines, serviceline)
                      .name
                  : 'all';
              const filename = `doctivity_SHARED_${
                before ? 'BEFORE' : 'AFTER'
              }_${provider.first_name}_${
                provider.last_name
              }_${servicelineName}_${moment().format('YY_MM_DD')}.csv`;
              try {
                const data = this.formatCSV(
                  response.data,
                  affiliatedResponse.data
                );
                fileDownload(data, filename);
              } catch (err) {
                console.error(`Could not format csv for ${filename}`);
                console.error(err);
                fileDownload(response.data, filename);
              }
            });
        });
    }
  }

  fetchSpecialtiesData() {
    this.props.dispatch(
      loadAnalytics({
        type: 'CLAIMS_NETWORK_SPECIALTIES',
        filter: {
          provider_id: this.props.provider.id,
          serviceline:
            this.state.serviceline === 'all' || this.state.provider2
              ? undefined
              : this.state.serviceline,
          provider_id_2: this.state.provider2
            ? this.state.provider2.id
            : undefined,
          before: this.state.before,
          patient_type: this.props.patientType,
        },
      })
    );
  }

  fetchNetworkAffiliated() {
    const { dispatch, network, clientId } = this.props;

    const { before } = this.state;

    if (network?.data?.rows) {
      const providerKey = before ? 'provider1' : 'provider2';
      const networkProviderIds = network.data.rows.map(
        (network) => network[providerKey].id
      );
      dispatch(
        queryProviders(
          {
            where: { id: networkProviderIds },
            include: [
              {
                association: 'clients',
                attributes: ['id'],
                through: { attributes: [] }, // don't need the join table data
                where: {
                  id: clientId,
                },
                duplicating: false,
                paranoid: false,
                required: false,
              },
            ],
          },
          API_PROVIDERS_LIST
        )
      );
    }
  }

  formatCSV(data, affiliated) {
    let columns = CsvUtil.stringToMatrix(data);
    const affiliatedIds = affiliated.map((affiliated) => affiliated.id);
    const providerNum = this.state.before ? '1' : '2';
    if (columns.length > 0) {
      let provider2IdIndex = -1;
      let provider_2_IdIndex = -1;
      let taxonomyIdIndex = -1;
      for (let index = 0; index < columns[0].length; index++) {
        if (columns[0][index] === `provider_${providerNum}_id`) {
          provider2IdIndex = index;
        } else if (columns[0][index] === `provider${providerNum}.id`) {
          provider_2_IdIndex = index;
        } else if (columns[0][index] === `provider${providerNum}.taxonomy.id`) {
          taxonomyIdIndex = index;
        } else if (columns[0][index] === 'pct1') {
          columns[0][index] = 'Percent 1';
        } else if (columns[0][index] === 'pct2') {
          columns[0][index] = 'Percent 2';
        } else if (columns[0][index] === 'sum_shared_patients') {
          columns[0][index] = 'Shared Patients';
        }
      }

      columns[0].push('Affiliated');
      for (let index = 1; index < columns.length - 1; index++) {
        if (columns[0].length > 0) {
          const providerIdIndex =
            provider2IdIndex > -1 ? provider2IdIndex : provider_2_IdIndex;
          const providerId = parseInt(columns[index][providerIdIndex]);
          if (providerId) {
            if (affiliatedIds.includes(providerId)) {
              columns[index].push('Yes');
            } else {
              columns[index].push('No');
            }
          } else {
            columns[index].push('No');
          }
        }
      }

      const idsToRemove = [
        provider2IdIndex,
        provider_2_IdIndex,
        taxonomyIdIndex,
      ]
        .filter((id) => id > -1)
        .sort((a, b) => b - a);
      idsToRemove.forEach((id) => {
        columns = DataUtil.removeMatrixColumn(columns, id);
      });
    }
    return CsvUtil.matrixToString(columns);
  }

  render() {
    const { classes, network, provider, providers } = this.props;
    const { provider2, serviceline, inView } = this.state;

    const networkAffiliatedData = StoreUtil.getData(providers);
    if (
      !inView ||
      !network ||
      !StoreUtil.isLoaded(network) ||
      (!provider2 &&
        (!networkAffiliatedData ||
          network.data.rows.length !== networkAffiliatedData.length))
    ) {
      return (
        <InView onChange={this.onInView}>
          <LoadingView />
        </InView>
      );
    }

    const data = [];
    const providerKey = this.state.before ? 'provider1' : 'provider2';
    if (StoreUtil.isLoaded(network)) {
      if (this.state.provider2) {
        // show tree map as servicelines within single provider
        network.data.rows.forEach((d) => {
          data.push({
            x: d.serviceline ? d.serviceline.name : 'loading...',
            y: parseInt(d.sum_shared_patients, 10),
            id: d[providerKey].id,
          });
        });
      } else {
        // show tree map as all providers, maybe within a serviceline
        network.data.rows.forEach((d) => {
          if (d[providerKey]) {
            const affiliated =
              networkAffiliatedData.find(
                (networkAffiliated) =>
                  networkAffiliated.id === d[providerKey].id
              )?.clients?.length > 0 || false;
            data.push({
              x: `${d[providerKey].last_name}, ${d[providerKey].first_name}`,
              y: parseInt(d.sum_shared_patients, 10),
              pct1: d.pct1,
              pct2: d.pct2,
              provider: d[providerKey],
              id: d[providerKey].id,
              fillColor: affiliated
                ? ColorUtil.AffiliatedColor
                : ColorUtil.NonAffiliatedColor,
              affiliated,
            });
          }
        });
      }
    }

    const series = [
      {
        data,
      },
    ];

    let contextMenu;

    const options = {
      legend: {
        show: false,
      },
      chart: {
        height: 350,
        type: 'treemap',
        toolbar: {
          show: false,
        },
        events: {
          click: (event, chartContext, config) => {
            if (this.state.provider2) {
              this.navigateToProvider(config);
            } else {
              this.onChartClick(event, chartContext, config);
            }
          },
          dataPointMouseEnter: (event, chartContext, config) => {
            contextMenu = new VanillaContextMenu({
              scope: document.querySelector('main'),
              customClass: classes.contextMenu,
              menuItems: [
                {
                  label: 'Open Link in New Tab',
                  callback: () => {
                    this.navigateToProvider(config, true);
                  },
                },
                {
                  label: 'Open Link in New Window',
                  callback: () => {
                    this.navigateToProvider(config, false, true);
                  },
                },
                {
                  label: 'Open Link',
                  callback: () => {
                    this.navigateToProvider(config);
                  },
                },
              ],
            });
            this.setState({ contextMenu: contextMenu });
          },
          dataPointMouseLeave: () => {
            contextMenu?.off();
          },
        },
        animations: {
          enabled: false,
          easing: 'easeinout',
          speed: 500,
          animateGradually: {
            enabled: true,
            delay: 75,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 150,
          },
        },
      },
      plotOptions: {
        treemap: {
          distributed: true,
          enableShades: false,
        },
      },
      tooltip: {
        custom: ({ dataPointIndex }) => {
          const d = series[0].data[dataPointIndex];
          if (d.provider) {
            let sharedPatients = '';
            if (this.state.serviceline !== 'all') {
              const servicelineName = ServiceLineUtil.findServiceLine(this.props.servicelines, this.state.serviceline).name;
              sharedPatients = `<div>${d.y.toLocaleString()} shared patients</div>`;
              if (this.state.before) {
                if (d.pct2) {
                  sharedPatients += `<div>${d.pct2}% of ${provider.last_name}, ${provider.first_name}'s [${servicelineName}] patients are first seen by ${d.provider.last_name}, ${d.provider.first_name}</div>`;
                }
                if (d.pct1) {
                  sharedPatients += `<div>${d.pct1}% of ${d.provider.last_name}, ${d.provider.first_name}'s [${servicelineName}] patients were later seen by ${provider.last_name}, ${provider.first_name}</div>`;
                }
              } else { // after
                if (d.pct1) {
                  sharedPatients += `<div>${d.pct1}% of ${provider.last_name}, ${provider.first_name}'s [${servicelineName}] patients are later seen by ${d.provider.last_name}, ${d.provider.first_name}</div>`;
                }
                if (d.pct2) {
                  sharedPatients += `<div>${d.pct2}% of ${d.provider.last_name}, ${d.provider.first_name}'s [${servicelineName}] patients were first seen by ${provider.last_name}, ${provider.first_name}</div>`;
                }

              }
            }
            return `<div style="padding:8px">
              <div><b>${d.x}</b></div>
              ${d.affiliated ? '<div>AFFILIATED<div>' : ''}
              <div>${SpecialtyUtil.getNameFromTaxonomy(
                d.provider.taxonomy
              )}</div>
              ${sharedPatients}`;
          }
          return `<div style="padding:8px;">
              <div><b>${d.x}</b></div>
              <div>${d.y.toLocaleString()} shared patients</div>
            </div>`;
        },
      },
    };

    let specialties = [];
    if (StoreUtil.isLoaded(this.props.specialties)) {
      specialties = StoreUtil.getData(this.props.specialties);
    }

    return (
      <InView onChange={this.onInView}>
        <Card className={classes.card} ref={this.cardElement}>
          <CardContent>
            <TitleTooltip
              title='Providers with Shared Patients'
              tooltip='Insight into relationships between providers based on their shared patients.'
              rightLabel={null && 'Based on claims from all ages'}
              onExport={
                serviceline !== 'all' ? () => this.fetchData(true) : undefined
              }
            />

            {this.state.provider2 ? (
              <div className={classes.provider2}>
                <Typography>
                  Service Lines for claims with
                  {this.state.before &&
                    ` ${this.props.provider.last_name}, ${this.props.provider.first_name} before `}
                </Typography>
                <SmallIconTextButton
                  icon='person'
                  text={`${this.state.provider2.last_name}, ${this.state.provider2.first_name}`}
                  onClick={this.navigateToProvider}
                />
                {!this.state.before &&
                  ` after ${this.props.provider.last_name}, ${this.props.provider.first_name}`}
              </div>
            ) : (
              <div className={classes.dropDownContainer}>
                <FormControl
                  variant='outlined'
                  className={classes.filterDropDown}
                  key='direction'
                >
                  <InputLabel className={classes.filterLabel}>
                    Claims Date
                  </InputLabel>
                  <Select
                    key='select'
                    value={this.state.before}
                    onChange={this.onDirectionChange}
                    label='Claims Date'
                  >
                    <MenuItem value={false} key='after'>
                      {`After ${provider.last_name}, ${provider.first_name}`}
                    </MenuItem>
                    <MenuItem value key='before'>
                      {`Before ${provider.last_name}, ${provider.first_name}`}
                    </MenuItem>
                  </Select>
                </FormControl>
                <div className={classes.dropDown}>
                  <ServiceLineSelect
                    value={this.state.serviceline}
                    onChange={this.onServicelineChange}
                    showSubServicelines
                    showAll
                  />
                </div>
                <FormControl
                  variant='outlined'
                  className={classes.specialtyDropDown}
                  key='specialty'
                >
                  <InputLabel className={classes.filterLabel}>
                    Specialty
                  </InputLabel>
                  <Select
                    key='select'
                    value={this.state.specialty}
                    onChange={this.onSpecialtyChange}
                    label='Specialty'
                  >
                    <MenuItem value='all' key='all'>
                      <em>All</em>
                    </MenuItem>
                    {specialties &&
                      specialties.map((s) => (
                        <MenuItem value={s.code} key={s.code}>
                          {SpecialtyUtil.getNameFromTaxonomy(s)}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <div className={classes.dropDown}>
                  <PatientTypeSelect noCapturedClaims />
                </div>
              </div>
            )}

            {network.data.rows.length === 0 && (
              <Typography className={classes.noData}>
                No shared patients.
              </Typography>
            )}
            {!this.state.provider2 && network.data.rows.length > 0 && (
              <div className={classes.colorKey}>
                <div
                  className={classes.colorKeyIcon}
                  style={{
                    backgroundColor: ColorUtil.AffiliatedColor,
                  }}
                />
                <div className={classes.colorKeyText}>Affiliated</div>

                <div
                  className={classes.colorKeyIcon}
                  style={{
                    backgroundColor: ColorUtil.NonAffiliatedColor,
                  }}
                />
                <div className={classes.colorKeyText}>Non-Affiliated</div>
              </div>
            )}
            {network.data.rows.length > 0 && (
              <Chart
                type='treemap'
                height={350}
                options={options}
                series={series}
              />
            )}
            {this.state.provider2 && (
              <SmallIconTextButton
                icon='chevron_left'
                text='Show All Providers'
                onClick={this.removeProvider2}
              />
            )}
          </CardContent>
        </Card>
      </InView>
    );
  }

  onChartClick = (event, chartContext, config) => {
    const { isMobile } = this.props;

    if (!isMobile && !this.state.provider2 && config.dataPointIndex > -1) {
      const providerKey = this.state.before ? 'provider1' : 'provider2';
      this.setState(
        {
          provider2:
            this.props.network.data.rows[config.dataPointIndex][providerKey],
          specialty: 'all',
        },
        this.fetchData
      );
    }
  };

  navigateToProvider = (config, newTab = false, newWindow = false) => {
    const { provider2, before } = this.state;
    const providerKey = before ? 'provider1' : 'provider2';
    // console.log('provider2: ', provider2);
    let selectedProvider = provider2;
    if (!selectedProvider) {
      selectedProvider = config && config.dataPointIndex > -1
        ? this.props.network.data.rows[config.dataPointIndex][providerKey]
        : undefined;
    }
    if (selectedProvider) {
      const url = `/providers/${selectedProvider.id}/market`;
      if (newTab) {
        window.open(url, '_blank');
      } else if (newWindow) {
        window.open(url, '_blank', 'rel=noopener noreferrer');
      } else {
        this.props.router.navigate(url);
      }
    }
  };

  removeProvider2 = () => {
    this.setState(
      {
        provider2: null,
      },
      this.fetchData
    );
  };

  onServicelineChange = (id) => {
    this.setState(
      {
        serviceline: id,
      },
      this.fetchAllData
    );
  };

  onDirectionChange = (event) => {
    this.setState(
      {
        before: event.target.value,
      },
      this.fetchAllData
    );
  };

  onSpecialtyChange = (event) => {
    this.setState(
      {
        specialty: event.target.value,
      },
      this.fetchData
    );
  };

  onInView = () => {
    this.setState({ inView: true });
  };
}

ClaimsNetwork.propTypes = {
  classes: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
  clientId: PropTypes.number.isRequired,
  isMobile: PropTypes.bool.isRequired,
  servicelines: PropTypes.array,
  provider: PropTypes.object,
  network: PropTypes.object,
  providers: PropTypes.object,
  specialties: PropTypes.object,
  patientType: PropTypes.oneOf(PatientTypes),
};

function mapStateToProps(state) {
  return {
    clientId: state.app.selectedClient,
    network: StoreUtil.get(state.analytics, 'CLAIMS_NETWORK'),
    specialties: StoreUtil.get(state.analytics, 'CLAIMS_NETWORK_SPECIALTIES'),
    servicelines: state.claims && state.claims.servicelines_grouped,
    providers: StoreUtil.get(state.providers, StoreUtil.COMMON_LIST),
    patientType:
      state.app.patientType === PatientType.CAPTURED_CLAIMS
        ? PatientType.ALL_PATIENTS
        : state.app.patientType,
  };
}

const styled = withStyles(styles)(withIsMobile(ClaimsNetwork));
const connected = connect(mapStateToProps)(styled);
const routed = withRouter(connected);
export { routed as ClaimsNetwork };
